import type { ThreadRuntimeMaterialization } from "@/lib/thread-sync-runtime-materialization"

export interface ThreadSyncResumeState {
  cursor: number
  tailCursor: number
  requestedAfter: number
  historyToken: string | null
  isCatchingUp: boolean
  syncReadyReceived: boolean
  lastPageCount: number
  lastPageBytes: number
}

export function buildHandshakeFrame(args: {
  cursor: number
  viewerActivityEnabled: boolean
}): {
  requestedAfter: number
  payload: {
    type: "sync.resume" | "sync.hello"
    after: number
    supportsViewerActivity?: boolean
    viewerActivityPending?: boolean
  }
} {
  const requestedAfter = args.cursor > 0 ? args.cursor : 0
  return {
    requestedAfter,
    payload: {
      type: requestedAfter > 0 ? "sync.resume" : "sync.hello",
      after: requestedAfter,
      supportsViewerActivity: args.viewerActivityEnabled || undefined,
      viewerActivityPending: args.viewerActivityEnabled || undefined,
    },
  }
}

export function prepareResumeForConnectionAttempt(current: ThreadSyncResumeState): ThreadSyncResumeState {
  return {
    ...current,
    requestedAfter: current.cursor,
    syncReadyReceived: false,
  }
}

export function resetResumeForDisconnect(current: ThreadSyncResumeState): ThreadSyncResumeState {
  return {
    ...current,
    isCatchingUp: false,
    syncReadyReceived: false,
    lastPageCount: 0,
    lastPageBytes: 0,
  }
}

export function resetResumeForColdReplay(): ThreadSyncResumeState {
  return {
    cursor: 0,
    tailCursor: 0,
    requestedAfter: 0,
    historyToken: null,
    isCatchingUp: false,
    syncReadyReceived: false,
    lastPageCount: 0,
    lastPageBytes: 0,
  }
}

export function restoreResumeFromPersistedValidatedSnapshot(args: {
  current: ThreadSyncResumeState
  cursor: number
  historyToken: string
}): ThreadSyncResumeState {
  const cursor = Math.max(0, Math.floor(args.cursor))
  return {
    ...args.current,
    cursor,
    tailCursor: cursor,
    requestedAfter: 0,
    historyToken: args.historyToken,
    isCatchingUp: false,
    syncReadyReceived: false,
    lastPageCount: 0,
    lastPageBytes: 0,
  }
}

export function markResumeConnectionClosed(current: ThreadSyncResumeState): ThreadSyncResumeState {
  return {
    ...current,
    isCatchingUp: false,
    syncReadyReceived: false,
  }
}

export function applySyncReadyFrame(
  current: ThreadSyncResumeState,
  frame: {
    acceptedCursor: number
    tailId: number
    historyToken: string | null
  },
): ThreadSyncResumeState {
  return {
    ...current,
    cursor: frame.acceptedCursor,
    tailCursor: frame.tailId,
    historyToken: frame.historyToken,
    isCatchingUp: frame.acceptedCursor < frame.tailId,
    syncReadyReceived: true,
  }
}

export function applyCatchupPageFrame(
  current: ThreadSyncResumeState,
  frame: {
    count: number
    approxBytes: number
    nextCursor: number
  },
  args?: { appliedWithError?: boolean },
): ThreadSyncResumeState {
  if (args?.appliedWithError) {
    return current
  }

  return {
    ...current,
    isCatchingUp: true,
    lastPageCount: frame.count,
    lastPageBytes: frame.approxBytes,
    cursor: Math.max(current.cursor, frame.nextCursor),
  }
}

export function applyCatchupCompleteFrame(
  current: ThreadSyncResumeState,
  frame: { finalCursor: number },
): ThreadSyncResumeState {
  return {
    ...current,
    cursor: Math.max(current.cursor, frame.finalCursor),
    isCatchingUp: false,
  }
}

export function applyLiveSyncEvent(
  current: ThreadSyncResumeState,
  args: { eventId: number; appliedWithError?: boolean },
): ThreadSyncResumeState {
  return {
    ...current,
    cursor: args.appliedWithError ? current.cursor : Math.max(current.cursor, args.eventId),
  }
}

export function evaluateSyncReadyContinuity(args: {
  historyContinuity: "contiguous" | "broken"
  previousHistoryToken: string | null
  nextHistoryToken: string | null
  acceptedCursor: number
  runtimeMaterialization: ThreadRuntimeMaterialization
  runtimeConnectionVersion: number | null
  connectionVersion: number
  activeLastEventId: number | null
  hasActiveRuntimeSnapshot: boolean
}): {
  hasCurrentConnectionAuthoritativeRuntime: boolean
  hasHistoryContinuityBroken: boolean
  hasHistoryTokenMismatch: boolean
  hasCursorRegression: boolean
  shouldResetRuntime: boolean
} {
  const hasCurrentConnectionAuthoritativeRuntime =
    (args.runtimeMaterialization === "authoritative" ||
      args.runtimeMaterialization === "authoritative_partial") &&
    args.runtimeConnectionVersion === args.connectionVersion

  const hasHistoryContinuityBroken = args.historyContinuity === "broken"

  const hasHistoryTokenMismatch =
    args.previousHistoryToken !== null && args.nextHistoryToken !== args.previousHistoryToken

  const hasStaleRuntimeSnapshotToInvalidate =
    args.hasActiveRuntimeSnapshot &&
    args.runtimeMaterialization !== "control_plane_only" &&
    !hasCurrentConnectionAuthoritativeRuntime

  const shouldResetForContinuityBreak =
    (hasHistoryContinuityBroken || hasHistoryTokenMismatch) &&
    (hasStaleRuntimeSnapshotToInvalidate || args.activeLastEventId !== null)

  const hasCursorRegression =
    args.activeLastEventId !== null && args.activeLastEventId > args.acceptedCursor
  const canColdReplayCoverUnvalidatedCursorRegression =
    hasCursorRegression &&
    args.acceptedCursor <= 0 &&
    args.previousHistoryToken === null
  const shouldResetForCursorRegression =
    hasCursorRegression && !canColdReplayCoverUnvalidatedCursorRegression

  return {
    hasCurrentConnectionAuthoritativeRuntime,
    hasHistoryContinuityBroken,
    hasHistoryTokenMismatch,
    hasCursorRegression,
    // A pure cursor regression at acceptedCursor=0 with no prior history token
    // means the server is about to replay the entire event stream for an
    // otherwise unvalidated in-memory display. Keeping that projection mounted
    // avoids a tab-switch flash to the loading transcript while the cold replay
    // idempotently catches up from the beginning. Regressions with a prior token
    // still flow through the reset path so the stale-cold-handshake hydration
    // guard in the manager can reconnect from the validated cursor.
    shouldResetRuntime: shouldResetForContinuityBreak || shouldResetForCursorRegression,
  }
}
